import { tabsOf, totalsOf } from '../order-tabs/order-tabs'
import { apiErrorField, apiErrorMessage } from '@agos/api-client'
import { useGetOrderTabs } from '@agos/api-client/orders'
import {
  getGetRevenueHandoversQueryKey,
  useDeleteRevenueHandoversHandoverIdHandled,
  useGetRevenueHandovers,
  usePostRevenueHandoversHandled,
  usePutRevenueHandoversHandoverIdHandled,
} from '@agos/api-client/payments'
import {
  T,
  cardPayOnlineLabel,
  formatCents,
  formatCount,
  formatDay,
  formatTime,
  parseMoneyToCents,
  qrPayFigure,
  qrPayLabel,
  toDateParam,
  tt,
  type MoneyInput,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  DateRangePicker,
  FilterBar,
  FilterSelect,
  PageHeader,
  ReadFailedBlock,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Banknote,
  Calculator,
  CheckCheck,
  CheckCircle2,
  CreditCard,
  Hash,
  Minus,
  QrCode,
  Receipt,
  RefreshCw,
  TrendingUp,
  Truck,
  Undo2,
  Wallet,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  auditHandover,
  batchOutcomeNote,
  driftDirection,
  filterHandovers,
  handoverTotals,
  isHandled,
  qrPayTotalCentsOf,
  unhandledSummary,
  walletQrPayFigureCents,
  type HandoverFilter,
  type HandoverReport,
} from './handover-report'

/**
 * `/revenue-handovers` — the shift closings on record.
 *
 * `manager-orders` owns the screen that creates one. This is the screen that
 * checks them, and checking is the part legacy does not do: it renders the stored
 * numbers and never compares them to what the register says was taken.
 *
 * FB-068 is what that misses. Across the 363 handovers in the restored data the
 * form's own arithmetic always reconciles — but in **103 of them** the card figure
 * the person entered disagrees with the wallet's own terminal-plus-courier total,
 * and each of those is cash that either was or was not handed over.
 *
 * Two additions to legacy's layout, both in service of that: a "Kontrola" column
 * saying whether a row holds together, and a filter narrowing to the rows that do
 * not. Everything else — the hero, the four payment-method cards, the sortable
 * table, the three-column detail panel below it — is legacy's, label for label.
 *
 * Ported from legacy `pages/revenue-handovers.tsx`.
 */

function today(): string {
  return toDateParam(new Date())
}

/**
 * What a confirmation is about to mark - §9.791. One record from its row or its detail, or every record the filter
 * shows that nobody has marked, from the batch button.
 */
type Marking =
  | { kind: 'one'; report: HandoverReport }
  | { kind: 'all'; ids: number[]; count: number; cashCents: number; drifting: number }

/** *Vybavil Peter Novák 15. 10. 21:40* - who took the envelope and when, as the row's title and the detail say it. */
function handledBy(report: HandoverReport): string {
  const who = report.handledByName ?? tt('iný zamestnanec')
  const when = report.handledAt ? `${formatDay(report.handledAt)} ${formatTime(report.handledAt)}` : ''
  return `${tt('Vybavil')} ${who} ${when}`.trim()
}

/** What *Kontrola* says about a record, as the marking confirmation warns about it - or null when it adds up. */
function auditWarning(report: HandoverReport): string | null {
  const audit = auditHandover(report)
  if (audit.arithmeticOff) return tt('Kontrola hlási, že hotovosť a karty nedávajú zadanú tržbu.')
  if (audit.cardDrifts) {
    return `${tt('Kontrola hlási rozdiel kariet oproti kase:')} ${formatCents(audit.cardDriftCents)}.`
  }
  return null
}

export function RevenueHandoversPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // Legacy opens on today only and the operator widens it from there. Kept: the
  // default range decides what the page claims the shift owes.
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const [filter, setFilter] = useState<HandoverFilter>('all')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const { data, isFetching, isError, error, refetch } = useGetRevenueHandovers({ from, to })
  /* §9.627, FB-936 - **this screen had no `isError` at all**, and `reports` below is `data ?? []`, so
   * a refusal rendered as *Žiadne reporty za zadané obdobie* - a range with nothing in it. The one
   * refusal it can now receive is §10A.316's, which says the range holds more records than the screen
   * can add up; showing that as an empty period would hide the fix behind the defect. */
  /* **The code, never the sentence** - §9.620's rule on the sibling refusal. §10A.316 answers
   * `HANDOVER_RANGE_TOO_WIDE` on that one 400, so this branch needs no Slovak of its own and a
   * reworded server message cannot silently turn it off. A cross-stack guard holds the two spellings
   * together. */
  const tooWide = apiErrorField(error, 'code') === 'HANDOVER_RANGE_TOO_WIDE'
  // §9.5 asks for the outstanding tab balance to be visible where the cash expectations are. It is read
  // here rather than computed: `/order-tabs` is the open ones, already filtered by the server.
  const openTabs = useGetOrderTabs()
  const openTabsCents = totalsOf(tabsOf(openTabs.data)).outstandingCents

  const reports: HandoverReport[] = useMemo(() => data ?? [], [data])
  const shown = useMemo(
    () => filterHandovers(reports, { filter, search }),
    [reports, filter, search],
  )
  const totals = handoverTotals(shown)
  // §9.791 - over the rows the filter shows, so the line, the button and the hero speak about the same records.
  const unhandled = unhandledSummary(shown)

  /* §9.791, owner O143 - *"Označiť ako Vybavené / tým pádom aj že sa to dá odoznačiť"*. Every mark and unmark asks
   * first; the server records who and when, and its audit line keeps that after an unmark. */
  const [marking, setMarking] = useState<Marking | null>(null)
  const [unmarking, setUnmarking] = useState<HandoverReport | null>(null)
  const [note, setNote] = useState('')
  const markOne = usePutRevenueHandoversHandoverIdHandled()
  const markAll = usePostRevenueHandoversHandled()
  const unmark = useDeleteRevenueHandoversHandoverIdHandled()
  const refresh = () => void queryClient.invalidateQueries({ queryKey: getGetRevenueHandoversQueryKey() })

  const askToMark = (next: Marking) => {
    setNote('')
    setMarking(next)
  }

  const confirmMarking = () => {
    if (marking === null) return
    const kept = note.trim() === '' ? undefined : note.trim()
    // A refusal is said in the server's words - a record somebody else marked a moment ago answers 409 naming them -
    // and the list is read again either way, so the screen catches up with whoever was faster.
    const failed = (caught: unknown) => {
      setMarking(null)
      refresh()
      toast({
        variant: 'destructive',
        title: tt('Uzávierku sa nepodarilo označiť'),
        description: apiErrorMessage(caught, tt('Server ju odmietol.')),
      })
    }
    if (marking.kind === 'one') {
      const handoverId = marking.report.id ?? 0
      markOne.mutate(
        { handoverId, data: { note: kept } },
        {
          onSuccess: () => {
            setMarking(null)
            refresh()
            toast({ title: tt('Označené ako vybavené') })
          },
          onError: failed,
        },
      )
      return
    }
    markAll.mutate(
      { data: { ids: marking.ids, note: kept } },
      {
        onSuccess: (outcome) => {
          setMarking(null)
          refresh()
          toast({ title: batchOutcomeNote(outcome) })
        },
        onError: failed,
      },
    )
  }

  const confirmUnmarking = () => {
    if (unmarking === null) return
    unmark.mutate(
      { handoverId: unmarking.id ?? 0 },
      {
        onSuccess: () => {
          setUnmarking(null)
          refresh()
          toast({ title: tt('Vybavenie zrušené') })
        },
        onError: (caught) => {
          setUnmarking(null)
          refresh()
          toast({
            variant: 'destructive',
            title: tt('Vybavenie sa nepodarilo zrušiť'),
            description: apiErrorMessage(caught, tt('Server ho odmietol.')),
          })
        },
      },
    )
  }

  const columns: Column<HandoverReport>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: tt('Dátum'),
        sortable: true,
        sortValue: (report) => new Date(report.createdAt ?? 0).getTime(),
        render: (report) => (
          <div>
            <div className="font-medium text-foreground">
              {report.createdAt ? formatDay(report.createdAt) : '-'}
            </div>
            <div className="text-xs text-muted-foreground">
              {report.createdAt ? formatTime(report.createdAt) : ''}
            </div>
          </div>
        ),
      },
      {
        key: 'employeeName',
        header: tt('Zamestnanec'),
        sortable: true,
        sortValue: (report) => report.employeeName,
        render: (report) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-xs shrink-0">
              {initials(report.employeeName)}
            </div>
            <span className="font-bold text-foreground text-sm truncate">
              {report.employeeName}
            </span>
          </div>
        ),
      },
      {
        key: 'totalRevenue',
        header: tt('Tržba'),
        sortable: true,
        sortValue: (report) => auditHandover(report).totalRevenueCents,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (report) => (
          <span className="font-bold text-foreground tabular-nums">
            {formatCents(auditHandover(report).totalRevenueCents)}
          </span>
        ),
      },
      {
        key: 'cashToHandOver',
        header: tt('Hotovosť'),
        sortable: true,
        sortValue: (report) => auditHandover(report).cashToHandOverCents,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (report) => {
          const cash = auditHandover(report).cashToHandOverCents
          return (
            <span
              className={cn(
                'text-lg font-black tabular-nums',
                cash < 0 ? 'text-red-400' : 'text-emerald-400',
              )}
            >
              {formatCents(cash)}
            </span>
          )
        },
      },
      {
        // Not in legacy. FB-068 — the reason for reviewing a handover at all.
        key: 'audit',
        // §9.177 — the owner asked what this column is, which is the answer: it did not say. The
        // header and every cell now name what was compared with what.
        header: tt('Kontrola'),
        headerTitle: tt(
          'Porovnáva zadané tržby s tým, čo hovorí pokladňa: súčet hotovosti a kariet proti celkovej sume, a zadané karty proti terminálu a kuriérom.',
        ),
        sortable: true,
        sortValue: (report) => Math.abs(auditHandover(report).cardDriftCents),
        render: (report) => {
          const audit = auditHandover(report)
          if (audit.arithmeticOff) {
            return (
              <StatusBadge
                color="red"
                label={tt('nesedí súčet')}
                icon={AlertTriangle}
                title={tt('Hotovosť a karty spolu nedávajú zadanú celkovú tržbu.')}
              />
            )
          }
          if (audit.cardDrifts) {
            return (
              <StatusBadge
                color="amber"
                label={`${tt('karty')} ${formatCents(audit.cardDriftCents)}`}
                icon={AlertTriangle}
                title={tt(
                  'Zadané karty sa líšia od súčtu terminálu a kuriérov o túto sumu - chýbajúca alebo prebytočná hotovosť.',
                )}
              />
            )
          }
          // Not an em dash: a dash reads as *not checked*, and this row was checked and is right.
          return (
            <span
              className="text-xs text-emerald-500"
              title={tt('Súčty sedia aj karty sedia s terminálom a kuriérmi.')}
            >
              {tt('sedí')}
            </span>
          )
        },
      },
      {
        // §9.791, owner O143. A record nobody has marked offers the button rather than a word, so marking an envelope
        // is two presses from the list - and the button opens the confirmation, not the row's detail.
        key: 'handled',
        header: tt('Stav'),
        headerTitle: tt('Či niekto prevzal hotovosť a označil uzávierku ako vybavenú.'),
        sortable: true,
        sortValue: (report) => (isHandled(report) ? 1 : 0),
        render: (report) =>
          isHandled(report) ? (
            <span data-handled={report.id}>
              <StatusBadge
                color="emerald"
                label={tt('Vybavené')}
                icon={CheckCircle2}
                title={[handledBy(report), report.handledNote ?? ''].filter(Boolean).join(' · ')}
              />
            </span>
          ) : (
            <button
              type="button"
              data-handover-mark={report.id}
              onClick={(event) => {
                event.stopPropagation()
                askToMark({ kind: 'one', report })
              }}
              className={cn(BUTTON_SECONDARY, 'px-2.5 py-1 rounded-lg text-xs')}
            >
              <T>Vybaviť</T>
            </button>
          ),
      },
      {
        key: 'orderCount',
        header: tt('Obj.'),
        sortable: true,
        sortValue: (report) => report.orderCount ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (report) => (
          <span className="text-muted-foreground font-medium tabular-nums">
            {report.orderCount ?? 0}
          </span>
        ),
      },
    ],
    [],
  )

  const expanded = shown.find((report) => report.id === expandedId)

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Odovzdanie tržby')}
        description={tt('Prehľad odovzdaných tržieb šoférov')}
        icon={<Truck className="w-5 h-5" />}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Meno, poznámka k výdaju...'),
        }}
        filters={
          <>
            <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
            <FilterSelect
              value={filter}
              label={tt('Zobraziť uzávierky')}
              onChange={(value) => setFilter(value as HandoverFilter)}
              options={[
                { value: 'all', label: tt('Všetky uzávierky') },
                { value: 'drifting', label: tt('Len nezhody s kasou') },
                // §9.791 - the default stays *Všetky*, so the figures the screen opens on do not move.
                { value: 'unhandled', label: tt('Len nevybavené') },
                { value: 'handled', label: tt('Len vybavené') },
              ]}
            />
          </>
        }
        actions={
          <button
            type="button"
            onClick={() =>
              void queryClient.invalidateQueries({ queryKey: getGetRevenueHandoversQueryKey() })
            }
            disabled={isFetching}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-xl',
              BUTTON_PRIMARY,
              'text-sm active:scale-95 transition-all disabled:opacity-50',
            )}
          >
            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            <T>Načítať</T>
          </button>
        }
      />

      {isError ? (
        /* **Replaces the figures rather than sitting above them - §9.552's structural lesson.** The
         * claim on this screen *is* the nine figures, and `handoverTotals` over an empty list is nine
         * noughts: a nought revenue on a handover screen reads as nobody having handed anything over,
         * which is FB-843's shape. The header and the range picker stay, because narrowing the range
         * is exactly what the refusal asks for. */
        <ReadFailedBlock
          marker="handovers"
          title={tooWide ? tt('Obdobie je príliš široké') : tt('Uzávierky sa nepodarilo načítať')}
          onRetry={tooWide ? undefined : () => void refetch()}
          icon={<AlertTriangle className="w-4 h-4" />}
        >
          {/* The server's own sentence for the refusal - it names how many records the range holds and
            * by how much to narrow it, which no sentence written here could. */}
          {tooWide
            ? apiErrorMessage(error, tt('Zúžte obdobie.'))
            : tt('Skúste to znova. Sumy sa zámerne nezobrazujú, kým nie je zoznam celý.')}
        </ReadFailedBlock>
      ) : (
        <>
      {shown.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4" data-handover-totals>
          <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between min-w-[220px]">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">
                <T>Hotovosť na odovzdanie</T>
              </p>
              <p
                className={cn(
                  'text-5xl font-black leading-none tracking-tight tabular-nums',
                  totals.cashCents < 0 ? 'text-red-400' : 'text-emerald-400',
                )}
              >
                {formatCents(totals.cashCents)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {totals.count} {reportWord(totals.count)} · {totals.orderCount} <T>objednávok</T>
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                <T>Celková tržba</T>
              </p>
              <p className="text-xl font-semibold text-foreground tabular-nums">
                {formatCents(totals.revenueCents)}
              </p>
            </div>
          </div>

          {/* A fifth tile takes a fifth column rather than a row of its own (the review of §9.741), and since §9.767
            * it is always there, so the grid is always five. */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <WalletCard
              label={tt('Hotovosť')}
              cents={totals.walletCashCents}
              icon={Banknote}
              tone="emerald"
            />
            <WalletCard
              label={tt('Karta terminál')}
              cents={totals.walletTerminalCents}
              icon={CreditCard}
              tone="blue"
            />
            <WalletCard
              label={tt('Kuriér karta')}
              cents={totals.walletCourierCents}
              icon={CreditCard}
              tone="orange"
            />
            <WalletCard
              label={cardPayOnlineLabel()}
              figure={formatCents(totals.walletOnlineCents)}
              icon={CreditCard}
              tone="violet"
              split="cardpay-online"
            />
            {/* §9.741, owner O112 - *"ad d QrPay, it would be much better to have it also in dovozdanie
              * tržby tab"*; §9.767, owner O129 - *"in odovzdanie trzby we have that prices overall on top - add
              * also there next to Cardpay online QRPay"*. **Always, beside CardPay online**: a 0,00 € is a period
              * with no QR Pay, and *neevidované* a period whose every record predates QR Pay being separated. */}
            <WalletCard
              label={qrPayLabel()}
              figure={qrPayFigure(walletQrPayFigureCents(totals))}
              icon={QrCode}
              tone="pink"
              split="qr"
              unrecorded={totals.qrPayUnrecordedCount}
              sub={
                totals.qrPayUnrecordedCount > 0 && totals.qrPayUnrecordedCount < totals.count
                  ? `${formatCount(totals.qrPayUnrecordedCount)} ${tt('záznamov bez QR Pay - ich QR platby sú v CardPay online')}`
                  : undefined
              }
            />
          </div>

          {/* §9.252 - the owner's item K: *"the tiles should also carry výplaty and nákupy for the
           * whole day, so the figure is the real cash."* The headline above already **is** real
           * cash — `cashToHandOver` is net of all four of these — so what was missing is what it is
           * net *of*. Every tile that was here before is still here; these sit under them, which is
           * the owner's own condition. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-handover-outgoings>
            <WalletCard
              label={tt('Výplaty')}
              cents={totals.payoutsCents}
              icon={Minus}
              tone="orange"
              testId="payouts"
            />
            <WalletCard
              label={tt('Nákupy')}
              cents={totals.purchasesCents}
              icon={Minus}
              tone="orange"
              testId="purchases"
            />
            <WalletCard
              label={tt('Tankovanie')}
              cents={totals.fuelCents}
              icon={Minus}
              tone="orange"
              testId="fuel"
            />
            <WalletCard
              label={tt('Lístky')}
              cents={totals.vouchersCents}
              icon={Minus}
              tone="orange"
              testId="vouchers"
            />
          </div>

          {/* A payout with no note is the one a week later nobody can explain. `payoutNotes` already
           * reads them; this says how many of the period's records carry one. */}
          {totals.payoutsCents > 0 && (
            <p
              className="text-[11px] text-muted-foreground"
              data-payout-notes={totals.annotatedPayouts}
            >
              <T>Poznámku k výplate má</T> {formatCount(totals.annotatedPayouts)} <T>z</T>{' '}
              {formatCount(totals.count)} <T>záznamov</T>.
            </p>
          )}
        </div>
      )}

      {/* §9.5: the outstanding tab balance, where the cash expectations are - with the thing about it that
       * is easy to misread. `OrderTabService`: an open tab is deliberately **not** part of what anybody
       * hands over, because the sale is not registered until the tab is paid. */}
      {openTabsCents > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-open-tabs>
          <Receipt className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Na otvorených účtoch je</T> <strong>{formatCents(openTabsCents)}</strong> -{' '}
            <T>
              do očakávanej hotovosti sa nepočíta, lebo objednávka na účte sa zaregistruje až pri
              úhrade.
            </T>
          </span>
        </p>
      )}

      {totals.driftingCount > 0 && (
        <p
          className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400"
          data-handover-drift
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          {/* FB-068 - each of these is cash that either was or was not handed
              over. Legacy renders the stored figures and never compares them. */}
          <span>
            <strong>
              {totals.driftingCount}/{totals.count}
            </strong>{' '}
            <T>uzávierok má odpočítanú sumu kariet inú, než zaznamenala kasa. Rozdiel spolu:</T>{' '}
            <strong>{formatCents(totals.netDriftCents)}</strong>
          </span>
        </p>
      )}

      {/* §9.791 - what nobody has taken yet, and one press to take all of it. Over the rows the filter shows, not one
        * page of the table, and absent when every shown record is handled. */}
      {unhandled.count > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
          data-handover-unhandled={unhandled.count}
        >
          <p className="text-sm text-foreground">
            <strong>
              <T>Nevybavené:</T> {formatCount(unhandled.count)} <T>z</T> {formatCount(shown.length)}
            </strong>{' '}
            {shown.length === 1 ? tt('uzávierky') : tt('uzávierok')} · <strong>{formatCents(unhandled.cashCents)}</strong>{' '}
            <T>hotovosti ešte nikto neprevzal</T>
          </p>
          <button
            type="button"
            data-handover-mark-all
            onClick={() =>
              askToMark({
                kind: 'all',
                ids: unhandled.ids,
                count: unhandled.count,
                cashCents: unhandled.cashCents,
                drifting: shown.filter((report) => !isHandled(report) && auditWarning(report) !== null)
                  .length,
              })
            }
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-xl text-sm',
              BUTTON_PRIMARY,
            )}
          >
            <CheckCheck className="w-4 h-4" />
            <T>Označiť zobrazené ako vybavené</T> ({formatCount(unhandled.count)})
          </button>
        </div>
      )}

      <DataTable<HandoverReport>
        columns={columns}
        data={shown}
        pageSize={20}
        rowKey={(report) => report.id ?? 0}
        onRowClick={(report) =>
          setExpandedId(expandedId === report.id ? null : (report.id ?? null))
        }
        emptyMessage={tt('Žiadne reporty za zadané obdobie.')}
        emptyIcon={<Wallet className="w-10 h-10 mb-3 opacity-40" />}
      />

      {expanded && (
        <HandoverDetail
          report={expanded}
          onClose={() => setExpandedId(null)}
          onMark={() => askToMark({ kind: 'one', report: expanded })}
          onUnmark={() => setUnmarking(expanded)}
        />
      )}
        </>
      )}

      <ConfirmDialog
        open={marking !== null}
        onClose={() => setMarking(null)}
        onConfirm={confirmMarking}
        loading={markOne.isPending || markAll.isPending}
        icon={<CheckCircle2 className="w-5 h-5" />}
        title={
          marking?.kind === 'all'
            ? tt('Označiť zobrazené uzávierky ako vybavené?')
            : tt('Označiť uzávierku ako vybavenú?')
        }
        description={
          marking === null
            ? undefined
            : marking.kind === 'all'
              ? `${tt('Uzávierok:')} ${formatCount(marking.count)} · ${tt('spolu')} ${formatCents(marking.cashCents)} ${tt('hotovosti. Tie, ktoré medzitým vybavil niekto iný, ostanú jeho.')}`
              : `${marking.report.employeeName ?? ''} · ${formatCents(auditHandover(marking.report).cashToHandOverCents)} ${tt('hotovosti.')}`
        }
        confirmLabel={tt('Označiť ako vybavené')}
      >
        <div className="space-y-3" data-handover-mark-dialog>
          {marking?.kind === 'one' && auditWarning(marking.report) !== null && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400" data-handover-mark-warning>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              <span>{auditWarning(marking.report)}</span>
            </p>
          )}
          {marking?.kind === 'all' && marking.drifting > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400" data-handover-mark-warning>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              <span>
                {formatCount(marking.drifting)} <T>z nich má podľa kontroly rozdiel s kasou.</T>
              </span>
            </p>
          )}
          <label className="block text-xs font-medium text-muted-foreground">
            <T>Poznámka (nepovinná)</T>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={500}
              rows={2}
              data-handover-mark-note
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={unmarking !== null}
        onClose={() => setUnmarking(null)}
        onConfirm={confirmUnmarking}
        loading={unmark.isPending}
        icon={<Undo2 className="w-5 h-5" />}
        title={tt('Zrušiť vybavenie?')}
        description={
          unmarking === null
            ? undefined
            : `${handledBy(unmarking)}. ${tt('Uzávierka bude znova nevybavená; kto ju vybavil, ostane zapísané v logu.')}`
        }
        confirmLabel={tt('Zrušiť vybavenie')}
      />
    </div>
  )
}

function HandoverDetail({
  report,
  onClose,
  onMark,
  onUnmark,
}: {
  report: HandoverReport
  onClose: () => void
  onMark: () => void
  onUnmark: () => void
}) {
  const audit = auditHandover(report)
  const handled = isHandled(report)

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-handover-detail>
      <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2 min-w-0">
          <Calculator className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">
            Detail: {report.employeeName} -{' '}
            {report.createdAt
              ? `${formatDay(report.createdAt)} ${formatTime(report.createdAt)}`
              : ''}
          </span>
        </h3>
        <button type="button" onClick={onClose} className={cn(BUTTON_GHOST, 'text-xs shrink-0')}>
          <T>Zavrieť</T>
        </button>
      </div>

      {/* §9.791 - the state in words, and the button that does the opposite of it. */}
      <div
        className="px-6 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3"
        data-handover-state={handled ? 'handled' : 'unhandled'}
      >
        {handled ? (
          <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-start gap-1.5 min-w-0">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {handledBy(report)}
              {report.handledNote ? (
                <span className="text-muted-foreground"> · {report.handledNote}</span>
              ) : null}
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            <T>Nikto ešte túto hotovosť neoznačil ako prevzatú.</T>
          </p>
        )}
        {handled ? (
          <button
            type="button"
            onClick={onUnmark}
            data-handover-unmark={report.id}
            className={cn(BUTTON_SECONDARY, 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs')}
          >
            <Undo2 className="w-3.5 h-3.5" />
            <T>Zrušiť vybavenie</T>
          </button>
        ) : (
          <button
            type="button"
            onClick={onMark}
            data-handover-detail-mark={report.id}
            className={cn(BUTTON_PRIMARY, 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs')}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <T>Označiť ako vybavené</T>
          </button>
        )}
      </div>

      <div className="px-6 pb-6 bg-muted/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
          <section className="space-y-3">
            <SectionHeading icon={TrendingUp} label={tt('Tržba podľa platby')} />
            <BreakdownRow tone="text-emerald-400" label={tt('Hotovosť')} value={report.cashTotal} />
            <BreakdownRow
              tone="text-blue-400"
              label={tt('Karta terminál')}
              value={report.cardTerminalTotal}
            />
            <BreakdownRow
              tone="text-orange-400"
              label={tt('Karta kuriér')}
              value={report.cardCourierTotal}
            />
            <BreakdownRow
              tone="text-violet-400"
              label={cardPayOnlineLabel()}
              value={report.cardOnlineTotal}
            />
            {/* §9.741 - the fifth part of these takings, so the rows still add up to *Celkom* below. §9.767 -
              * always, beside CardPay online: *neevidované* on a record from before QR Pay was separated,
              * because a nought there would claim the day was checked for QR. */}
            <QrPayRow report={report} marker="qr-takings" />
            <div className="pt-2 border-t border-border flex justify-between items-center">
              <span className="text-xs font-bold text-muted-foreground">
                <T>Celkom</T>
              </span>
              <span className="text-sm font-black text-foreground tabular-nums">
                {formatCents(audit.totalRevenueCents)}
              </span>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeading icon={Minus} label={tt('Odrátky')} />
            {/* §9.767 - the online card's one name; this row said *CardPay*. */}
            <BreakdownRow
              tone="text-violet-400"
              label={cardPayOnlineLabel()}
              value={report.cardPayAmount}
              marker="cardpay-deduction"
            />
            {/* §9.741 - taken off automatically beside CardPay online since §10A.390, and `deductionCents`
              * counts it, so *Celkom odrátky* below is the sum of the rows drawn here. Always (§9.767). */}
            <QrPayRow report={report} marker="qr-deduction" />
            <BreakdownRow tone="text-blue-400" label={tt('Karta')} value={report.cardAmount} />
            {/* §9.456 - the owner: *"So in Odovozdanie trzby make sure also karta kuriér will
              * be"*. The type has carried this since FB-068 and §9.452's audit sentence blames it
              * by name - *"v poli Karta u kuriéra je 0"* - while this panel drew eight rows and
              * not this one, so the field the message is about was the field the screen would not
              * show.
              *
              * **`BreakdownRow` and not `OptionalRow`, which is the opposite call from *Nákup 2*.**
              * A zero here is the information: it is what says the courier's card money went into
              * the *Karta* box instead. Hiding it would hide the whole point. */}
            <BreakdownRow
              tone="text-orange-400"
              label={tt('Karta kuriér')}
              value={report.cardCourierAmount}
            />
            <BreakdownRow
              tone="text-orange-400"
              label={tt('Lístky')}
              value={report.vouchersAmount}
            />
            <BreakdownRow
              tone="text-yellow-400"
              label={tt('Tankovanie')}
              value={report.fuelAmount}
            />
            <BreakdownRow
              tone="text-teal-400"
              label={tt('Nákup 1')}
              value={report.purchase1Amount}
            />
            {/* Legacy hides the second slots when they are zero - a handover with
                nothing bought should not read as though something was. */}
            <OptionalRow
              tone="text-teal-400"
              label={tt('Nákup 2')}
              value={report.purchase2Amount}
            />
            <OptionalRow
              tone="text-pink-400"
              label={withNote(tt('Výplata'), report.payout1Note)}
              value={report.payout1Amount}
            />
            <OptionalRow
              tone="text-pink-400"
              label={withNote(tt('Výplata 2'), report.payout2Note)}
              value={report.payout2Amount}
            />
            <div className="pt-2 border-t border-border flex justify-between items-center">
              <span className="text-xs font-bold text-muted-foreground">
                <T>Celkom odrátky</T>
              </span>
              <span className="text-sm font-black text-red-400 tabular-nums">
                -{formatCents(audit.deductionCents)}
              </span>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading icon={Wallet} label={tt('Výsledok')} />

            <div
              className={cn(
                'rounded-2xl p-5 border flex flex-col items-center justify-center text-center',
                audit.cashToHandOverCents < 0
                  ? 'bg-red-500/8 border-red-500/20'
                  : 'bg-emerald-500/8 border-emerald-500/20',
              )}
            >
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <T>Hotovosť na odovzdanie</T>
              </p>
              <p
                className={cn(
                  'text-4xl font-black leading-none tabular-nums',
                  audit.cashToHandOverCents < 0 ? 'text-red-400' : 'text-emerald-400',
                )}
              >
                {formatCents(audit.cashToHandOverCents)}
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3 text-xs space-y-1.5">
              <div className="flex justify-between text-muted-foreground">
                <span>
                  <T>Celková tržba</T>
                </span>
                <span className="text-foreground font-semibold tabular-nums">
                  {formatCents(audit.totalRevenueCents)}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>
                  <T>Odrátky</T>
                </span>
                <span className="text-red-400 font-semibold tabular-nums">
                  -{formatCents(audit.deductionCents)}
                </span>
              </div>
              <div className="border-t border-border pt-1.5 flex justify-between font-bold">
                <span className="text-muted-foreground">
                  <T>= Hotovosť</T>
                </span>
                <span
                  className={cn(
                    'tabular-nums',
                    audit.cashToHandOverCents < 0 ? 'text-red-400' : 'text-emerald-400',
                  )}
                >
                  {formatCents(audit.cashToHandOverCents)}
                </span>
              </div>
            </div>

            {audit.arithmeticOff && (
              <p className="text-xs text-red-400 leading-relaxed" data-handover-arithmetic>
                <T>Záznam nesedí - tržba mínus odrátky dáva</T>{' '}
                <strong>{formatCents(audit.expectedCashCents)}</strong>, <T>uložené je</T>{' '}
                {formatCents(audit.cashToHandOverCents)}.
              </p>
            )}

            {audit.cardDrifts && (
              <p
                className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed"
                data-handover-card-drift
              >
                {/* Spelled out in both directions: which way it went decides
                    whether the till comes up short or over. */}
                <T>Kasa zaznamenala na kartách</T> {formatCents(audit.walletCardCents)},{' '}
                <T>obsluha odpočítala</T> {formatCents(audit.enteredCardCents)} -{' '}
                {driftDirection(audit.cardDriftCents)} ({formatCents(audit.cardDriftCents)}).{' '}
                {/* §9.452, FB-772 - two readings now. The wrong-box case has moved out of this
                 * block entirely (§9.460): it is no longer a drift, so this sentence never runs for
                 * it, and printing *the kasa recorded 0,00 on cards* beside *the kasa recorded
                 * 124,20 at the courier* was one sentence contradicting itself. */}
                {audit.doorStated ? (
                  <T>
                    Záznam uvádza kartu u kuriéra zvlášť, takže sa porovnáva terminál s terminálom.
                  </T>
                ) : (
                  <T>
                    Záznam nemá pole pre kartu u kuriéra, takže sa buď prilepí ku karte, alebo sa
                    neodpočíta vôbec.
                  </T>
                )}
              </p>
            )}

            {/* §9.460 - **on its own, and not amber.** The owner's record had every euro recorded
              * and was being reported as a 124,20 € difference. This says the one true thing about
              * it: the money is there, in the wrong box. Muted rather than a warning colour,
              * because nothing is missing and a filing note that looks like a money alarm is how
              * *2/3 uzávierok má odpočítanú sumu kariet inú* reached the owner's screen. */}
            {audit.doorCardInTheWrongBox && (
              <p
                className="text-xs text-muted-foreground leading-relaxed"
                data-handover-wrong-box
              >
                <T>Karta u kuriéra je zapísaná v poli Karta - kasa zaznamenala u kuriéra</T>{' '}
                {formatCents(audit.doorWalletCents)}
                {', '}
                <T>
                  a v poli Karta u kuriéra je 0. Peniaze sú zapísané celé, len v nesprávnom poli -
                  hotovosť na odovzdanie je správna.
                </T>
              </p>
            )}

            <div className="flex items-center gap-1.5 text-xs text-muted-foreground justify-center">
              <Hash className="w-3 h-3" />
              <span>
                {report.orderCount ?? 0} <T>objednávok</T>
              </span>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

const WALLET_TONES = {
  emerald: 'bg-emerald-500/8 border-emerald-500/20 text-emerald-400',
  blue: 'bg-blue-500/8 border-blue-500/20 text-blue-400',
  orange: 'bg-orange-500/8 border-orange-500/20 text-orange-400',
  violet: 'bg-violet-500/8 border-violet-500/20 text-violet-400',
  /** §9.741 - QR Pay's own, the pink it has on every screen, so the fifth tile is not taken for a card. */
  pink: 'bg-pink-500/8 border-pink-500/20 text-pink-400',
} as const

function WalletCard({
  label,
  cents,
  figure,
  icon: Icon,
  tone,
  testId,
  split,
  sub,
  unrecorded,
}: {
  label: string
  /** The amount; `figure` instead when the tile draws a figure already written - QR Pay's *neevidované*. */
  cents?: number
  figure?: string
  icon: typeof Banknote
  tone: keyof typeof WALLET_TONES
  /** §9.252 — only the outgoings carry one; the four wallet tiles had none and still do not. */
  testId?: string
  /** §9.741, §9.767 - the CardPay online and QR Pay tiles carry one, so a check can find them side by side. */
  split?: string
  /** §9.767 - a line under the figure, said only when the period mixes records with and without QR Pay. */
  sub?: string
  /** §9.767 - the QR Pay tile's count of records from before QR Pay was separated. */
  unrecorded?: number
}) {
  return (
    <div
      className={cn('border rounded-2xl p-4 flex flex-col gap-1', WALLET_TONES[tone])}
      data-outgoing={testId}
      data-wallet-split={split}
      data-qr-unrecorded={unrecorded}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black leading-none tabular-nums">{figure ?? formatCents(cents ?? 0)}</p>
      {sub !== undefined && <p className="text-[11px] text-muted-foreground leading-snug">{sub}</p>}
    </div>
  )
}

function SectionHeading({ icon: Icon, label }: { icon: typeof Wallet; label: string }) {
  return (
    <h4 className="text-[11px] text-muted-foreground uppercase tracking-widest font-bold flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </h4>
  )
}

function BreakdownRow({
  tone,
  label,
  value,
  marker,
}: {
  tone: string
  label: string
  value: MoneyInput
  /** §9.741, §9.767 - the QR Pay rows and the CardPay online deduction carry one, so a check can find them. */
  marker?: string
}) {
  return (
    <div className="flex items-center justify-between gap-2" data-breakdown={marker}>
      <div className="flex items-center gap-2 min-w-0">
        <div className={cn('w-2 h-2 rounded-full shrink-0 bg-current', tone)} />
        <span className="text-xs text-muted-foreground truncate">{label}</span>
      </div>
      <span className={cn('text-sm font-bold shrink-0 tabular-nums', tone)}>
        {formatCents(parseMoneyToCents(value))}
      </span>
    </div>
  )
}

/** A deduction slot legacy leaves out of the panel entirely when it is zero. */
function OptionalRow(props: { tone: string; label: string; value: MoneyInput; marker?: string }) {
  if (parseMoneyToCents(props.value) <= 0) return null
  return <BreakdownRow {...props} />
}

/**
 * The record's QR Pay row - FRONTEND.md §9.767: drawn always, beside CardPay online, and *neevidované* rather than a
 * nought on a record from before QR Pay was separated (`qrPayTotalCentsOf` answers `null` there).
 */
function QrPayRow({ report, marker }: { report: HandoverReport; marker: string }) {
  return (
    <div className="flex items-center justify-between gap-2" data-breakdown={marker}>
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-2 h-2 rounded-full shrink-0 bg-current text-pink-400" />
        <span className="text-xs text-muted-foreground truncate">{qrPayLabel()}</span>
      </div>
      <span className="text-sm font-bold shrink-0 tabular-nums text-pink-400">
        {qrPayFigure(qrPayTotalCentsOf(report))}
      </span>
    </div>
  )
}

function withNote(label: string, note: string | null | undefined): string {
  return note && note.trim() !== '' ? `${label} (${note.trim()})` : label
}

function initials(name: string | undefined): string {
  return (name ?? '')
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
}

/**
 * `1 report`, `2 reporty`, `0` and `5+` `reportov` — Slovak counts in three, and
 * zero goes with the fives rather than the twos.
 */
function reportWord(count: number): string {
  if (count === 1) return tt('report')
  if (count >= 2 && count <= 4) return tt('reporty')
  return tt('reportov')
}
